import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  AssetService,
  type ListAssetsParams,
  type ListGalleryParams,
} from '../../services';
import { queryKeys } from '../keys';
import type { LifecycleState, UploadImageBase64Request } from '../../domain';

export function useFolders() {
  return useQuery({
    queryKey: queryKeys.folders(),
    queryFn: () => AssetService.getFolders(),
  });
}

export function useAssets(filter?: ListAssetsParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.assets(filter),
    queryFn: () => AssetService.list(filter),
    enabled: options?.enabled ?? true,
  });
}

export function useAsset(imageId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.asset(imageId ?? ''),
    queryFn: () => AssetService.getImage(imageId!),
    enabled: !!imageId,
  });
}

export function useGallery(params?: ListGalleryParams) {
  return useQuery({
    queryKey: queryKeys.gallery(params),
    queryFn: () => AssetService.listGallery(params),
  });
}

export function useUserAssets(filter?: ListAssetsParams) {
  return useQuery({
    queryKey: ['assets', 'user', filter ?? {}] as const,
    queryFn: () => AssetService.listUserAssets(filter),
  });
}

export function useAssetFamily(assetId: string | undefined, depth?: number) {
  return useQuery({
    queryKey: queryKeys.assetFamily(assetId ?? '', depth),
    queryFn: () => AssetService.getFamily(assetId!, depth),
    enabled: !!assetId,
  });
}

export function useSetLifecycleState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, state }: { assetId: string; state: LifecycleState | string }) =>
      AssetService.setLifecycleState(assetId, state),
    onSuccess: (_data, { assetId }) => {
      // asset(id) is a prefix of assetFamily(id), so this also refreshes lineage.
      qc.invalidateQueries({ queryKey: queryKeys.asset(assetId) });
      qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useUploadImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      sessionId,
      lineage,
    }: {
      file: File;
      sessionId?: string;
      lineage?: { parentAssetId: string; relationshipType: string };
    }) => AssetService.uploadImage(file, sessionId, lineage),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      if (vars.sessionId) {
        qc.invalidateQueries({ queryKey: queryKeys.sessionAssets(vars.sessionId) });
      }
    },
  });
}

export function useUploadImageBase64() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: UploadImageBase64Request) =>
      AssetService.uploadImageBase64(request),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      if (vars.sessionId) {
        qc.invalidateQueries({ queryKey: queryKeys.sessionAssets(vars.sessionId) });
      }
    },
  });
}

export function useDeleteImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (imageId: string) => AssetService.deleteImage(imageId),
    onSuccess: (_data, imageId) => {
      qc.invalidateQueries({ queryKey: queryKeys.asset(imageId) });
      qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}
